import React from "react";
import SEO from "react-seo-component";
import { Container } from "reactstrap";
import { Link } from "react-router-dom";
import FeatherIcon from "feather-icons-react";
import "./MoveGroups.css";

const groups = [
  {
    id: 1,
    title: "Small Household Items & Light Moves",
    desc:
      "1 to 5 items - appliances, single furniture pieces and small loads for one person.",
    examples: "Fridge, washing machine, microwave, single couch, suitcases, boxes",
    category: "small-household-light-moves",
  },
  {
    id: 2,
    title: "One‑Bedroom Apartment Moves",
    desc: "Full inventory of a 1‑bed residence - compact apartment moves.",
    examples: "Bed, couch, fridge, TV, desk, 5‑20 boxes",
    category: "one-bedroom-apartment-moves",
  },
  {
    id: 3,
    title: "Two‑Bedroom Household Moves",
    desc: "Complete 2‑bed home inventory - larger domestic loads.",
    examples: "Two beds, couches, dining table, wardrobes, 10‑30 boxes",
    category: "two-bedroom-household-moves",
  },
  {
    id: 4,
    title: "Three to Four Bedroom Full House Moves",
    desc: "High‑volume household moves that may require large trucks or multiple trips.",
    examples: "Multiple beds, couches, appliances, garage contents, 20+ boxes",
    category: "three-four-bedroom-full-house-moves",
  },
  {
    id: 5,
    title: "Large Office, Shop Fittings & Commercial Moves",
    desc: "B2B moves with office equipment, fixtures and recurring schedules.",
    examples: "Desks, chairs, monitors, printers, shop fixtures",
    category: "office-shop-fittings-commercial-moves",
  },
  {
    id: 6,
    title: "Retail, Distribution & Repetitive Stock Deliveries",
    desc: "Pallets and repeat stock deliveries for retail and wholesale clients.",
    examples: "Pallets, crates, groceries, FMCG, bulk boxes",
    category: "retail-distribution-repetitive-stock-deliveries",
  },
  {
    id: 7,
    title: "Construction, Industrial & Building Materials",
    desc: "Heavy and bulky construction materials and machinery.",
    examples: "Bricks, sand, tiles, timber, steel sheets, cement",
    category: "construction-industrial-building-materials",
  },
  {
    id: 8,
    title: "Heavy Specialty Items & Non‑Standard Loads",
    desc: "Oversized, fragile or specialist items requiring special handling.",
    examples: "Pianos, pool tables, large signage, arcade machines",
    category: "heavy-specialty-non-standard-loads",
  },
  {
    id: 9,
    title: "Multi‑pickup, Multi‑drop & Complex Route Jobs",
    desc: "Multi‑stop collections or deliveries and return‑trip logistics.",
    examples: "Several pickups, multi‑stop student moves, return trips",
    category: "multi-pickup-multi-drop-complex-route-jobs",
  },
  {
    id: 10,
    title: "Agricultural, Garden, Livestock & Outdoor Deliveries",
    desc: "Seasonal and rural deliveries for farm, garden and livestock needs.",
    examples: "Feed bags, plants, garden supplies, chopped wood",
    category: "agricultural-garden-livestock-outdoor-deliveries",
  },
];

const iconForGroup = {
  1: "box",
  2: "home",
  3: "home",
  4: "truck",
  5: "briefcase",
  6: "shopping-bag",
  7: "tool",
  8: "alert-triangle",
  9: "map",
  10: "leaf",
};

const classForGroup = {
  1: "g1",
  2: "g2",
  3: "g3",
  4: "g4",
  5: "g5",
  6: "g6",
  7: "g7",
  8: "g8",
  9: "g9",
  10: "g10",
};

const MoveGroups = () => {
  return (
    <React.Fragment>
      <SEO
        title={'Move Groups - QikTruck service categories'}
        titleTemplate={'QikTruck'}
        description={'Browse common move and delivery groups handled by QikTruck: household, commercial, construction, agricultural and specialised loads. Use this page to quickly estimate service type and vehicle needs.'}
        image={'https://media.qiktruck.co.za/qiktruck-preview-header.png'}
        pathname={'https://qiktruck.co.za/services/move-groups'}
        twitterUsername={'qiktrucksa'}
      />

      <section id="move-groups" className="move-groups" aria-label="Move service categories">
        <Container>
          <div className="mg-header">
            <h3 className="mg-title">Service & Move Groups</h3>
            <p className="mg-subtitle">
              Common load categories we handle - useful for instant pricing and vehicle selection.
            </p>
          </div>

          <div className="mg-grid" role="list">
            {groups.map((g) => (
              <article className={`mg-card ${classForGroup[g.id]}`} role="listitem" aria-labelledby={`mg-${g.id}`} key={g.id}>
                <div className="mg-card-header">
                  <span className={`mg-icon ${classForGroup[g.id]}`} aria-hidden="true">
                    <FeatherIcon icon={iconForGroup[g.id]} size={20} className="mg-svg-icon" />
                  </span>
                  <h4 id={`mg-${g.id}`} className="mg-card-title">{g.title}</h4>
                </div>
                <p className="mg-card-desc">{g.desc}</p>
                <div className="mg-card-footer">
                  <small>Examples: {g.examples}</small>
                </div>
                <Link
                  to={`/?category=${encodeURIComponent(g.category)}`}
                  className="mg-card-link"
                  aria-label={`Explore ${g.title}`}
                />
              </article>
            ))}
          </div>
        </Container>
      </section>

      {/* JSON-LD structured data for SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "QikTruck Move Groups",
          "itemListElement": groups.map((g, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "name": g.title,
            "description": g.desc,
          })),
        })}
      </script>
    </React.Fragment>
  );
};

export default MoveGroups;
